import {NucleotideType} from 'biojs/Util';
import {MonomerVisualSymbol} from 'graphql-client/__generated/graphqlGlobalTypes';
import {MonomerQuery} from 'molbio/monomers/__generated/MonomerQuery';
import {
  BaseMonomerUpdate,
  SharedMonomerCsvValidationOutput,
  SharedMonomerValidationOutput,
  BaseMonomerValidationFields,
} from 'molbio/monomers/SharedMonomerConstants';
import {Operator} from 'search/SearchConstants';
import {getJSONPromise, patchJSONPromise, postJSONPromise, putJSONPromise, deletePromise} from 'util/Net';

type Monomer = MonomerQuery['monomers'][number];

export const MONOMER_LIBRARY_URL = '/monomers';

export async function validateHELMQuery(
  helmString: string,
  operator: Operator,
  nucleotideType: NucleotideType
): Promise<{error: string}> {
  return await postJSONPromise({
    url: '/1/api/monomers:validate-filter-helm',
    data: {helmString, operator, nucleotideType},
  });
}

export const API_NATURAL_ANALOGUES = [
  'r',
  'd',
  'p',
  'A',
  'C',
  'G',
  'T',
  'U',
  'N',
  'R',
  'Y',
  'M',
  'K',
  'W',
  'S',
  'B',
  'D',
  'H',
  'V',
] as const;
export type APINaturalAnalogue = (typeof API_NATURAL_ANALOGUES)[number];
export interface APIMonomer {
  name: string;
  symbol: string;
  naturalAnalogue: APINaturalAnalogue;
  smiles: string;
  visualSymbol?: MonomerVisualSymbol;
  color?: string;
  customMolecularWeight?: number | null;
}

export async function createMonomers(
  monomers: Array<APIMonomer>,
  fromHelmLib: boolean = false
): Promise<Array<Monomer>> {
  return await postJSONPromise({
    url: '/1/api/monomers',
    data: {monomers, fromHelmLib},
  });
}

export async function createMonomersFromFile(url: string): Promise<Array<Monomer>> {
  return await postJSONPromise({
    url: '/1/api/monomers:bulk-create-from-csv',
    data: {csvUrl: url},
  });
}

export interface AliasData {
  id: number;
  syntaxId: number;
  token: string;
  helmToken: string;
  tokenVariantStart: string | null;
  tokenVariantEnd: string | null;
  sugarMonomerId: string;
  baseMonomerId: string;
  phosphateMonomerId: string | null;
}

export async function getIDTAliases(): Promise<Array<AliasData>> {
  return await getJSONPromise('/1/api/monomers/vendor-aliases/IDT');
}

export async function createIDTAliases(aliasCSVData: string): Promise<Array<AliasData>> {
  return await postJSONPromise({
    url: '/1/api/monomers/vendor-aliases/IDT',
    data: {aliasCSVData},
  });
}

export interface MonomerUpdate extends BaseMonomerUpdate {
  // Nucleotide-only; amino-acid monomers have no visual symbol.
  visualSymbol?: MonomerVisualSymbol;
}

export async function updateMonomer(monomerId: string, data: MonomerUpdate): Promise<APIMonomer> {
  return await patchJSONPromise({
    url: `/1/api/monomers/${monomerId}`,
    data,
  });
}

export enum SyntaxConfigurationKey {
  PREFILTER_TERMINALS = 'PREFILTER_TERMINALS',
  NULLIFY_ENDING_PHOSPHATE = 'NULLIFY_ENDING_PHOSPHATE',
  USE_SHARED_DELIMITER = 'USE_SHARED_DELIMITER',
}

export interface VendorModificationSyntax {
  id: number;
  apiId: string;
  name: string;
  isCaseSensitive: boolean;
  configuration: {
    [SyntaxConfigurationKey.NULLIFY_ENDING_PHOSPHATE]?: {};
    [SyntaxConfigurationKey.USE_SHARED_DELIMITER]?: {delimiter: string};
    [SyntaxConfigurationKey.PREFILTER_TERMINALS]?: {
      five_prime_left_delimiter?: string;
      five_prime_right_delimiter: string;
      three_prime_left_delimiter: string;
      three_prime_right_delimiter?: string;
      max_depth?: number;
    };
  };
  aliases: Array<AliasData>;
}

export async function getCustomSyntaxes(): Promise<Array<VendorModificationSyntax>> {
  return await getJSONPromise(`/1/api/vendor-syntaxes`);
}

export async function createCustomSyntax(
  data: Omit<VendorModificationSyntax, 'id' | 'apiId' | 'aliases'>
): Promise<VendorModificationSyntax> {
  return await postJSONPromise({
    url: '/1/api/vendor-syntaxes',
    data,
  });
}

export async function createAliases(syntaxId: number, aliasCSVData: string): Promise<Array<AliasData>> {
  return await postJSONPromise({
    url: `/1/api/vendor-syntaxes/${syntaxId}/aliases`,
    data: {aliasCSVData},
  });
}

export interface AliasDataForUpdate {
  token: string;
  sugarMonomerId: string;
  baseMonomerId: string;
  phosphateMonomerId: string | null;
  tokenVariantStart?: string | null;
  tokenVariantEnd?: string | null;
}

export async function updateAlias(
  syntaxId: number,
  aliasId: number,
  data: Omit<AliasData, 'id' | 'syntaxId' | 'helmToken'>
): Promise<AliasData> {
  return await putJSONPromise({url: `/1/api/vendor-syntaxes/${syntaxId}/aliases/${aliasId}`, data});
}

export async function deleteCustomSyntaxAlias(syntaxId: number, aliasId: number): Promise<void> {
  return await deletePromise({
    url: `/1/api/vendor-syntaxes/${syntaxId}/aliases/${aliasId}:delete`,
  });
}

export async function archiveMonomers(monomerIds: Array<string>, purpose: string): Promise<void> {
  return await postJSONPromise({
    url: `/1/api/monomers:archive`,
    data: {purpose, monomerIds},
  });
}

export async function unarchiveMonomers(monomerIds: Array<string>): Promise<void> {
  return await postJSONPromise({
    url: `/1/api/monomers:unarchive`,
    data: {monomerIds},
  });
}

// Keep monomer validation shapes in sync with those in benchling/molbio/monomers/validator.py
export interface MonomerValidationInput extends BaseMonomerValidationFields {
  naturalAnalogue?: APINaturalAnalogue | null;
}

export async function validateMonomerInput(
  monomerDicts: Array<MonomerValidationInput>
): Promise<Array<SharedMonomerValidationOutput>> {
  return await postJSONPromise({
    url: '/1/api/monomers:validate',
    data: {monomerDicts},
  });
}

export type MonomerCsvValidationOutput = SharedMonomerCsvValidationOutput;

export async function validateMonomersFromFile(
  filename: string,
  url: string
): Promise<MonomerCsvValidationOutput> {
  return await postJSONPromise({
    url: '/1/api/monomers:validate-csv',
    data: {filename, s3Location: url},
  });
}

export async function exportMonomersAsCSV(forReimportIntoBenchling: boolean): Promise<{
  s3Location: string;
}> {
  return await postJSONPromise({
    url: '/1/api/monomers:export-as-csv',
    data: {forReimportIntoBenchling},
  });
}

export async function exportCustomSyntaxesAsCSV(): Promise<{
  s3Location: string;
}> {
  return await postJSONPromise({
    url: '/1/api/vendor-syntaxes:export-as-csv',
  });
}
